import {gql} from '@apollo/client';

import {assert, assertAndContinue} from 'Asserts';
import {SchemaTag} from 'folder-items/FileConstants';
import {doQuerySafe, readQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {PlateCreationTableRowMetadata} from 'notebook/integrations/samples/PlateCreationStructuredTableAdapter';
import {ColumnGroup} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {LiveCellValue} from 'notebook/structured-tables/LiveCellValues';
import {
  bulkFetchParentStorableCapacityModel,
  isParentStorableCapacityInfo,
  isUnfinalizedParentStorableSample,
  ParentStorableCapacityInfo,
  readParentStorableCapacityFromCache,
} from 'notebook/structured-tables/row-updaters/inventoryParentStorableRowUpdaterUtils';
import {buildStandardUpdatedCells} from 'notebook/structured-tables/row-updaters/updateRowUtils';
import {
  SampleLookupInfo,
  StructuredTableColumn,
  StructuredTableRow,
  StructuredTableRowUpdater,
} from 'notebook/structured-tables/StructuredTableConstants';
import isColumnForSchemaField from 'notebook/structured-tables/util/isColumnForSchemaField';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {
  convertTagToTypedValue,
  getTypedValueFromStorableSummaries,
} from 'notebook/tables/typed-values/DataTypes';
import APIUtil from 'util/APIUtil';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {
  BulkFetchPlateInfoForRowQuery,
  BulkFetchPlateInfoForRowQueryVariables,
} from './__generated/BulkFetchPlateInfoForRowQuery';

type PlateInfo = ExtractByTypename<BulkFetchPlateInfoForRowQuery['storables'][number], 'PlateNode'>;

export const BULK_PLATE_INFO_FOR_ROW_UPDATE_QUERY = gql`
  query BulkFetchPlateInfoForRowQuery($plateIds: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    storables(ids: $plateIds) {
      ...PlateLiveDataGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('PlateLiveData').fragment}
  ${getFragmentWrapper('Location').fragment}
`;

async function _bulkFetchModel(
  sampleLookupInfos: Array<SampleLookupInfo>
): Promise<Array<PlateInfo | ParentStorableCapacityInfo | InaccessibleInfoGraphQL>> {
  const parentStorablesPromise = bulkFetchParentStorableCapacityModel(
    sampleLookupInfos
      .filter((info) => isUnfinalizedParentStorableSample(info))
      .map((info) => info.idForLookup)
  );
  const platesPromise = doQuerySafe<BulkFetchPlateInfoForRowQuery, BulkFetchPlateInfoForRowQueryVariables>(
    {query: BULK_PLATE_INFO_FOR_ROW_UPDATE_QUERY},
    {
      variables: {
        plateIds: sampleLookupInfos
          .filter((info) => !isUnfinalizedParentStorableSample(info))
          .map((info) => info.idForLookup),
      },
      fetchPolicy: 'network-only',
    }
  );
  const [parentStorables, {storables}] = await Promise.all([parentStorablesPromise, platesPromise]);
  // We know none of these will be a ContainerNode
  return [...(storables as Array<PlateInfo | InaccessibleInfoGraphQL>), ...parentStorables];
}

const PlateCreationTableRowUpdater: StructuredTableRowUpdater<
  PlateInfo | ParentStorableCapacityInfo,
  PlateCreationTableRowMetadata
> = {
  fetchModel(
    sampleLookupInfo: SampleLookupInfo
  ): Promise<PlateInfo | ParentStorableCapacityInfo | InaccessibleInfoGraphQL> {
    return _bulkFetchModel([sampleLookupInfo]).then((results) => results[0]);
  },

  bulkFetchModel(
    sampleLookupInfos: Array<SampleLookupInfo>
  ): Promise<Array<PlateInfo | ParentStorableCapacityInfo | InaccessibleInfoGraphQL>> {
    return _bulkFetchModel(sampleLookupInfos);
  },

  updateCell: (
    model: PlateInfo | ParentStorableCapacityInfo | InaccessibleInfoGraphQL,
    columnGroup: ColumnGroup | null,
    column: StructuredTableColumn
  ): LiveCellValue => {
    // No updates to cells for capacity info, which is used only in unfinalized rows & doesn't affect the
    // information stored in the table
    if (isParentStorableCapacityInfo(model)) {
      return 'NO_VALUE';
    }
    const plate = model;
    if (isInaccessible(plate)) {
      return 'NO_VALUE';
    }
    const location = plate.parentLocation;
    assert(location != null, "Plate's location should be specified in plate creation table.");

    if (column.columnType === TableColumnType.PLATE) {
      return getTypedValueFromStorableSummaries([plate]);
    }
    if (column.columnType === TableColumnType.PARENT_STORABLE) {
      if (isInaccessible(location)) {
        // TODO(BNCH-18256): Handle refresh/stale indicators if location is inaccessible.
        // Currently, we will not show stale indicators in this column, and refreshing will no-op for this column
        return 'NO_VALUE';
      }
      assert(location.__typename === 'LocationNode', 'Plate can only be stored in a location.');

      return getTypedValueFromStorableSummaries([location]);
    }

    const matchingTag = plate.tagsJson.find((tag: SchemaTag) =>
      isColumnForSchemaField(column, tag.tagSchemaField)
    );
    if (matchingTag) {
      return convertTagToTypedValue(matchingTag);
    }

    return 'NO_VALUE';
  },

  updateRow(
    model: PlateInfo | ParentStorableCapacityInfo | InaccessibleInfoGraphQL,
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<PlateCreationTableRowMetadata>,
    columnGroup: ColumnGroup | null
  ): StructuredTableRow<PlateCreationTableRowMetadata> {
    if (isInaccessible(model)) {
      return row;
    }
    return {
      ...row,
      cells: buildStandardUpdatedCells(model, columns, row, columnGroup, this.updateCell),
    };
  },

  readFromCache(sampleLookupInfo: SampleLookupInfo): PlateInfo | ParentStorableCapacityInfo | null {
    const {idForLookup: apiIdentifier} = sampleLookupInfo;
    if (isUnfinalizedParentStorableSample(sampleLookupInfo)) {
      return readParentStorableCapacityFromCache(apiIdentifier);
    }
    assertAndContinue(
      APIUtil.isPlate(apiIdentifier),
      'apiIdentifier must be for plate when updating Plate Creation Table'
    );
    if (!APIUtil.isPlate(apiIdentifier)) {
      return null;
    }

    const data = readQuerySafe<BulkFetchPlateInfoForRowQuery, BulkFetchPlateInfoForRowQueryVariables>(
      {query: BULK_PLATE_INFO_FOR_ROW_UPDATE_QUERY},
      {variables: {plateIds: [apiIdentifier]}, fetchPolicy: 'cache-only'}
    );
    if (data == null || !data.storables?.[0] || isInaccessible(data.storables[0])) {
      return null;
    }
    const plate = data.storables[0];

    assert(plate.__typename === 'PlateNode', 'Query must return plate for plate id');
    return plate;
  },
};

export default PlateCreationTableRowUpdater;
